/**
 * TradegateAuditPanel - Auditor en vivo de descartes de Tradegate.
 *
 * Desde feb 2026 solo se registra el motivo `fx_deviation` (Tradegate ya solo se usa
 * como fuente de precio en tiempo real, nunca de cambio). Los motivos frozen /
 * sign_mismatch / magnitude_divergence quedaron obsoletos con el refactor autoritativo.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const reasonMeta = {
  fx_deviation: { label: 'FX desviada > 1.5%', color: '#a855f7', hint: 'Tasa implícita EUR/USD del precio Tradegate ≠ tasa real' },
};

const TradegateAuditPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAudit = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/tradegate-audit`, {
        headers: authHeaders(),
        timeout: 10000,
      });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error cargando auditor');
    } finally {
      setLoading(false);
    }
  }, []);

  const resetAudit = async () => {
    if (!window.confirm('¿Reiniciar contadores del auditor?')) return;
    try {
      await axios.post(`${API}/api/admin/tradegate-audit/reset`, {}, { headers: authHeaders() });
      fetchAudit();
    } catch { /* silencioso */ }
  };

  useEffect(() => {
    fetchAudit();
    const interval = setInterval(fetchAudit, 30 * 1000); // refresco cada 30s
    return () => clearInterval(interval);
  }, [fetchAudit]);

  if (loading && !data) {
    return (
      <div className="p-8 text-center text-slate-400">
        <div className="inline-block w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p>Cargando auditor…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-400 mb-3">{error}</p>
        <button onClick={fetchAudit} className="px-4 py-2 rounded-lg bg-zinc-900/60 text-zinc-100 border border-gold-500/40 text-sm">
          Reintentar
        </button>
      </div>
    );
  }

  const totalChecked = data?.total_checked || 0;
  const totalStale = data?.total_stale || 0;
  const totalOk = data?.total_ok || 0;
  const stalePct = data?.stale_rate_pct || 0;
  const byReason = data?.by_reason || {};
  const topTickers = data?.top_tickers || [];
  const samples = data?.recent_samples || [];

  return (
    <div className="space-y-6" data-testid="tradegate-audit-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Auditor Stale de Tradegate</h3>
          <p className="text-xs text-slate-500">
            Contadores en memoria del proceso backend
            {data?.started_at && (
              <> · desde {new Date(data.started_at).toLocaleString('es-ES')}</>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchAudit}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm text-slate-200"
            title="Refrescar ahora"
          >
            <RefreshCw className="w-4 h-4" /> Refrescar
          </button>
          <button
            onClick={resetAudit}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-sm text-red-300"
            title="Reiniciar contadores"
          >
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-1">Total evaluados</p>
          <p className="text-2xl font-bold text-white">{totalChecked}</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-1">Tradegate aceptado</p>
          <p className="text-2xl font-bold text-green-400">{totalOk}</p>
        </div>
        <div className="bg-slate-800/50 border border-red-500/30 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-1">Tradegate descartado</p>
          <p className="text-2xl font-bold text-red-400">{totalStale}</p>
        </div>
        <div className="bg-slate-800/50 border border-gold-500/30 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-1">% descartes</p>
          <p className="text-2xl font-bold text-gold-400">{stalePct}%</p>
        </div>
      </div>

      {/* Desglose por motivo */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-white mb-3">Desglose por motivo</h4>
        <div className="space-y-3">
          {Object.entries(reasonMeta).map(([key, meta]) => {
            const count = byReason[key] || 0;
            const pct = totalStale ? (count / totalStale * 100).toFixed(1) : 0;
            return (
              <div key={key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">
                    {meta.label}
                    <span className="ml-2 text-[11px] text-slate-500">{meta.hint}</span>
                  </span>
                  <span className="text-white font-mono">{count} ({pct}%)</span>
                </div>
                <div className="h-2 bg-slate-900 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ background: meta.color, width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top tickers con más descartes */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-white mb-3">Top tickers con más descartes</h4>
        {topTickers.length === 0 ? (
          <p className="text-sm text-slate-500">Sin descartes registrados aún.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-700">
                  <th className="py-2 pr-4">Ticker</th>
                  <th className="py-2 pr-4">Descartes</th>
                  <th className="py-2 pr-4">Motivos</th>
                  <th className="py-2">Último evento</th>
                </tr>
              </thead>
              <tbody>
                {topTickers.map(t => (
                  <tr key={t.ticker} className="border-b border-slate-800">
                    <td className="py-2 pr-4 font-mono font-bold text-gold-400">{t.ticker}</td>
                    <td className="py-2 pr-4 font-mono text-white">{t.stale}</td>
                    <td className="py-2 pr-4 text-xs text-slate-300">
                      {Object.entries(t.reasons || {}).map(([r, c]) => (
                        <span
                          key={r}
                          className="inline-block mr-2 px-2 py-0.5 rounded-full text-[10px]"
                          style={{ background: `${reasonMeta[r]?.color || '#666'}22`, color: reasonMeta[r]?.color || '#aaa' }}
                        >
                          {r}: {c}
                        </span>
                      ))}
                    </td>
                    <td className="py-2 text-[11px] text-slate-500">
                      {t.last_seen ? new Date(t.last_seen).toLocaleString('es-ES') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Muestras recientes */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-white mb-3">Últimas 30 muestras stale</h4>
        {samples.length === 0 ? (
          <p className="text-sm text-slate-500">Sin muestras recientes.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {samples.map((s, i) => {
              const meta = reasonMeta[s.reason];
              return (
                <div
                  key={i}
                  className="p-2 rounded-lg bg-slate-900/50 border text-xs"
                  style={{ borderColor: meta ? `${meta.color}55` : 'rgb(51,65,85)' }}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono font-bold text-gold-400">{s.ticker}</span>
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px]"
                      style={{
                        background: `${meta?.color || '#666'}22`,
                        color: meta?.color || '#aaa',
                      }}
                      title={meta?.hint || ''}
                    >
                      {meta?.label || s.reason}
                    </span>
                    <span className="text-slate-500 ml-auto">{s.at ? new Date(s.at).toLocaleTimeString('es-ES') : ''}</span>
                  </div>
                  <div className="text-slate-400 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <span>TG: <span className="text-white font-mono">{s.tg_price?.toFixed?.(2) ?? s.tg_price} €</span></span>
                    <span>YF: <span className="text-white font-mono">{s.yf_price?.toFixed?.(2) ?? s.yf_price} {s.currency}</span></span>
                    <span>Δ FX: <span className="text-white font-mono">
                      {typeof s.tg_price === 'number' && typeof s.yf_price === 'number' && s.yf_price > 0
                        ? `${(((s.tg_price / s.yf_price) - (1 / (s.currency === 'USD' ? 1.08 : 1))) * 100).toFixed(2)}%`
                        : '—'}
                    </span></span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TradegateAuditPanel;
